import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createApiClient } from '@/api/client';
import { createChatApi } from '@/api/chat';
import { createChatStreamApi } from '@/api/chat-stream';
import { createFeedbackApi } from '@/api/feedback';
import { createSessionHistoryApi } from '@/api/session-history';
import { getFriendlyErrorMessage, getLabel } from '@/i18n/maps';
import { ApiError } from '@/utils/errors';
import { ChatContextProvider } from './ChatContext';
import { Composer } from './Composer';
import { classify } from './error-policy';
import { FeedbackReasonDialog } from './FeedbackReasonDialog';
import { MessageList } from './MessageList';
import { ProductPreviewPane } from './ProductPreviewPane';
import { Greeting } from './Greeting';
import { useChatSession } from './useChatSession';
import { useFeedbackReasons } from './useFeedbackReasons';
import { useShellWidth, WIDE_SHELL_MIN_WIDTH } from './useShellWidth';
import { mergeProviderPayloads } from '@/utils/providerPayload';
import { ART } from '@/art';
import type { ChatProps, Locale } from '@/types/widget';
import type { Product } from '@/types/chat';


export function ChatWidget(props: ChatProps): JSX.Element {
  const locale = props.locale ?? 'fr';
  const composerRef = useRef<HTMLDivElement | null>(null);

  const { api, streamApi, feedbackApi, historyApi } = useMemo(() => {
    const client = createApiClient({ baseUrl: props.apiBaseUrl, apiKey: props.apiKey });
    return {
      api: createChatApi(client),
      streamApi: createChatStreamApi(client),
      feedbackApi: createFeedbackApi(client),
      historyApi: createSessionHistoryApi(client),
    };
  }, [props.apiBaseUrl, props.apiKey]);

  // `?question=…` on the chat page starts a NEW conversation with that question,
  // so the stored session isn't restored in that case.
  const [startQuestion] = useState(readQuestionParam);
  const session = useChatSession(api, {
    initialSessionId: startQuestion ? undefined : props.initialSessionId,
    onSessionIdChange: props.onSessionIdChange,
    disableOverviewEcho: props.disableOverviewEcho,
    streamApi,
    historyApi,
  });

  const askedStartQuestion = useRef(false);
  // the waiting indicator's avatar, ready before the first question
  useEffect(() => {
    new Image().src = ART.typingAvatar;
  }, []);

  useEffect(() => {
    if (!startQuestion || askedStartQuestion.current) return;
    askedStartQuestion.current = true;
    dropQuestionParam(); // a page refresh must not ask it again
    void session.send(startQuestion);
  }, [startQuestion, session]);

  // Reason chips: fetched once per (api, locale). Silent-fail — the
  // two-step feedback flow still works without them (users just can't
  // pick a reason after the initial no-reason down).
  const { reasons: feedbackReasons } = useFeedbackReasons(feedbackApi, locale);

  // Dialog target: which backend message id the reason dialog is open for.
  // Null = closed. FeedbackButtons opens it synchronously on 👎 click
  // (before the initial no-reason down PUT even fires) so users never
  // see network latency between clicking and the dialog appearing.
  const [feedbackDialogTarget, setFeedbackDialogTarget] = useState<string | null>(null);
  const openFeedbackDialog = useCallback((backendMessageId: string) => {
    setFeedbackDialogTarget(backendMessageId);
  }, []);
  const closeFeedbackDialog = useCallback(() => setFeedbackDialogTarget(null), []);

  // Follow-up replies can reference a product shown in an earlier turn
  // without returning it again in the current provider_payload.
  const sessionPayload = useMemo(() => {
    const payloads = session.messages
      .filter((message) => message.type === 'assistant')
      .map((message) => message.response.provider_payload)
      .reverse();
    return mergeProviderPayloads(...payloads, session.restoredPayload);
  }, [session.messages, session.restoredPayload]);

  // Per-message write queue: both the button (initial "no-reason down")
  // and the dialog (submit with reason + comment) enqueue here, so if
  // the user is fast enough to submit the dialog before the initial PUT
  // lands, the reason PUT still executes AFTER it. Without this, the
  // slow initial PUT could arrive last and wipe the reason.
  const feedbackWriteChainsRef = useRef<Map<string, Promise<unknown>>>(new Map());
  const enqueueFeedbackWrite = useCallback(
    <T,>(messageId: string, fn: () => Promise<T>): Promise<T> => {
      const chains = feedbackWriteChainsRef.current;
      const prev = chains.get(messageId) ?? Promise.resolve();
      const next = prev.catch(() => undefined).then(fn);
      chains.set(messageId, next);
      return next;
    },
    [],
  );

  // Track which product (by slug) is currently being previewed in the side
  // panel. The product object itself is looked up across all assistant
  // messages' provider_payloads — a slug can appear in any message.
  const [previewSlug, setPreviewSlug] = useState<string | null>(null);
  const previewProduct = useMemo<Product | null>(() => {
    if (!previewSlug) return null;
    return sessionPayload.products.find((product) => product.slug === previewSlug) ?? null;
  }, [previewSlug, sessionPayload.products]);

  const closePreview = useCallback(() => setPreviewSlug(null), []);
  const openPreview = useCallback((slug: string) => setPreviewSlug(slug), []);

  // The format picked per product, shared by its card(s) and the preview pane.
  const [chosenVariants, setChosenVariants] = useState<Record<string, string>>({});
  const chooseVariant = useCallback(
    (slug: string, variantId: string) => setChosenVariants((m) => ({ ...m, [slug]: variantId })),
    [],
  );

  // Measure the chat shell so we can decide between side panel and drawer
  // based on the WIDGET's own width (not the viewport). Avoids the CSS
  // @container + flex pitfalls that left the drawer always-on in
  // production.
  const { ref: shellRef, width: shellWidth } = useShellWidth();
  const isWide = shellWidth != null && shellWidth >= WIDE_SHELL_MIN_WIDTH;

  return (
    <ChatContextProvider
      payload={sessionPayload}
      locale={locale}
      onAddToCart={props.onAddToCart}
      onProductDetailsClick={props.onProductDetailsClick}
      productPageUrl={props.productPageUrl}
      onPreviewProduct={openPreview}
      shellWidth={shellWidth}
      feedbackApi={feedbackApi}
      sessionId={session.sessionId}
      openFeedbackDialog={openFeedbackDialog}
      enqueueFeedbackWrite={enqueueFeedbackWrite}
      onRetryMessage={session.retry}
      sending={session.sending}
      chosenVariants={chosenVariants}
      chooseVariant={chooseVariant}
    >
      <div className="calebasse-chatbot-root cal-fragment">
        <div
          ref={shellRef}
          // Keep the shell in natural document flow. The composer stays at
          // the viewport bottom while the chat is visible, then follows the
          // shell normally when the user scrolls past it.
          className="cal-chat-shell tw-flex tw-flex-col tw-w-full"
        >
          {session.initializing && session.messages.length === 0 ? (
            <SessionLoading locale={locale} />
          ) : (
            <MessageList
              messages={session.messages}
              composerRef={composerRef}
              greeting={<Greeting />}
            />
          )}
          {/* Session-level failure (auth expired, rate limited, ...). Errors that
              belong in the message list instead (e.g. RESPONSE_GENERATION_FAILED)
              already render as a failed bubble via MessageList/FailedBubble —
              classify(...).banner is what keeps this from double-reporting those. */}
          {session.error && classify(session.error).banner && (
            <ChatErrorBanner
              error={session.error}
              retryAfterSeconds={session.retryAfterSeconds}
              locale={locale}
            />
          )}
          {/* History restore failed for a genuine transport reason (not
              SESSION_NOT_FOUND, not a session-level error). session.error is
              also set here, but useChatSession's restore-catch only flips
              restoreFailed on when classify(e).banner is false — a
              session-level restore failure (expired key, rate limit) takes
              the branch above instead, never this one. That guarantees the
              two banners never originate from the same restore attempt —
              it does NOT make them mutually exclusive overall. restoreFailed
              is sticky (only a fresh restore clears it) while session.error
              is set independently by later send()/retry() failures, so a
              send that hits a banner-worthy error shortly after a restore
              failure can still show both strips at once. useChatSession
              clears restoreFailed as soon as a send or retry actually gets
              a response, so this is a "won't linger once the conversation
              is clearly working" guarantee, not a "can never overlap" one. */}
          {session.restoreFailed && (
            <div
              role="alert"
              className="tw-px-3 tw-py-2 tw-text-xs tw-text-red-700 tw-bg-red-50 tw-border-t tw-border-red-200 tw-flex tw-items-center tw-justify-between tw-gap-3"
            >
              <span>{getLabel('history_load_failed', locale)}</span>
              <button
                type="button"
                onClick={session.reloadHistory}
                disabled={session.sending}
                className="tw-flex-shrink-0 tw-font-semibold tw-underline disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                {getLabel('retry', locale)}
              </button>
            </div>
          )}
          <div ref={composerRef} className="tw-sticky tw-bottom-0 tw-bg-white tw-z-10 tw-mt-auto">
            <Composer
              onSend={session.send}
              // Stop 只在真的有请求可中止时出现。恢复期间 abortRef 是空的，
              // 按钮是死的（点击无效，直到 5s 客户端超时自己触发）。
              onAbort={session.sending ? session.abort : undefined}
              disabled={session.initializing || session.sending}
            />
          </div>
        </div>
      </div>

      {/* Preview portal — rendered to document.body to escape any
          host-page ancestor that might create a containing block
          (transform, contain, filter, etc.) and confine a `fixed` panel
          to a subtree. The ChatContextProvider context is preserved
          across the portal. */}
      {previewProduct &&
        typeof document !== 'undefined' &&
        createPortal(
          isWide ? (
            <FixedSidePanel product={previewProduct} onClose={closePreview} />
          ) : (
            <BottomDrawer product={previewProduct} onClose={closePreview} />
          ),
          document.body,
        )}

      {/* Feedback reason dialog — portaled so its fixed backdrop escapes
          any host containing block, same rationale as the preview panel.
          z-index 43 sits above the product card (41) and the FAB (42). */}
      {typeof document !== 'undefined' &&
        createPortal(
          <FeedbackReasonDialog
            messageBackendId={feedbackDialogTarget}
            sessionId={session.sessionId}
            api={feedbackApi}
            reasons={feedbackReasons}
            locale={locale}
            onClose={closeFeedbackDialog}
            enqueueWrite={enqueueFeedbackWrite}
          />,
          document.body,
        )}
    </ChatContextProvider>
  );
}

function readQuestionParam(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('question')?.trim() || null;
}

function dropQuestionParam(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('question');
  window.history.replaceState(window.history.state, '', url);
}

function SessionLoading({ locale }: { locale: Locale }): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className="tw-max-w-[800px] tw-mx-auto tw-px-4 tw-py-6 tw-text-sm tw-text-cal-green-700 tw-flex tw-items-center tw-gap-3"
    >
      <span className="tw-inline-flex tw-gap-1" aria-hidden="true">
        {[0, 0.2, 0.4].map((delay) => (
          <span
            key={delay}
            className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-cal-green-700"
            style={{ animation: `cal-pulse 1.4s ease-in-out ${delay}s infinite` }}
          />
        ))}
      </span>
      <span>{getLabel('session_loading', locale)}</span>
    </div>
  );
}

/**
 * Desktop preview: pinned to the right edge of the viewport via fixed
 * positioning so it stays visible as the user scrolls the host page.
 * The wrapper is sized by content with a hard 100vh cap — `aside` inside
 * absorbs the cap through `overflow-y-auto` on its body.
 */
function FixedSidePanel({
  product,
  onClose,
}: {
  product: Product;
  onClose: () => void;
}): JSX.Element {
  return (
    <div className="calebasse-chatbot-root cal-fragment">
      <div
        // Desktop side panel: always 100vh, anchored top-right of the
        // viewport. `fillHeight` tells PPP to use the flex pattern so
        // the footer is pinned to the bottom of the panel (CTAs always
        // at the bottom edge of the viewport).
        className="tw-fixed tw-top-0 tw-right-0 tw-h-screen tw-w-80 lg:tw-w-96 tw-flex tw-flex-col tw-shadow-cal-lg"
        style={{ zIndex: 41 }}
      >
        <ProductPreviewPane product={product} onClose={onClose} fillHeight />
      </div>
    </div>
  );
}

/**
 * Mobile preview: bottom-anchored sheet with backdrop. Capped at 85vh so
 * the assistant message above remains glanceable. Header + footer are
 * `flex-shrink-0` inside ProductPreviewPane, the body is the only thing
 * that can shrink → CTAs are always visible at the bottom of the sheet.
 */
function BottomDrawer({
  product,
  onClose,
}: {
  product: Product;
  onClose: () => void;
}): JSX.Element {
  return (
    <div className="calebasse-chatbot-root cal-fragment">
      <div
        className="tw-fixed tw-inset-0 tw-bg-black/40 tw-flex tw-flex-col tw-justify-end"
        style={{ zIndex: 41 }}
        onClick={onClose}
      >
        <div
          className="tw-w-full tw-bg-white tw-rounded-t-2xl tw-shadow-2xl tw-flex tw-flex-col tw-overflow-hidden"
          style={{ maxHeight: '85vh' }}
          onClick={(e) => e.stopPropagation()}
        >
          <ProductPreviewPane product={product} onClose={onClose} />
        </div>
      </div>
    </div>
  );
}

function ChatErrorBanner({
  error,
  retryAfterSeconds,
  locale,
}: {
  error: Error;
  retryAfterSeconds: number | null;
  locale: Locale;
}): JSX.Element {
  const code = error instanceof ApiError ? error.code : undefined;
  const baseText = getFriendlyErrorMessage(code, error.name, locale);
  const suffix = retryAfterSeconds && retryAfterSeconds > 0 ? ` (${retryAfterSeconds}s)` : '';
  return (
    <div
      role="alert"
      className="tw-px-3 tw-py-2 tw-text-xs tw-text-red-700 tw-bg-red-50 tw-border-t tw-border-red-200"
    >
      {baseText}
      {suffix}
    </div>
  );
}
